import type { WaTemplateButton, WaTemplateMediaType } from "@/data/whatsappTemplateMocks";
import { ExternalLink, Phone, Reply, Image, Video, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

interface WhatsAppPreviewProps {
  body: string;
  footer?: string;
  mediaType: WaTemplateMediaType;
  mediaPreviewUrl?: string;
  headerText?: string;
  buttons: WaTemplateButton[];
  variableValues: Record<string, string>;
}

const resolveVariables = (text: string, values: Record<string, string>) => {
  let resolved = text;
  Object.entries(values).forEach(([key, value]) => {
    resolved = resolved.split(key).join(value || key);
  });
  return resolved;
};

const MediaPlaceholder = ({ type, url }: { type: WaTemplateMediaType; url?: string }) => {
  const { t } = useTranslation("settings");
  if (type === 'none') return null;

  if (type === 'image' && url) {
    return (
      <div className="w-full h-36 rounded-t-lg overflow-hidden">
        <img src={url} alt="Preview" className="w-full h-full object-cover" />
      </div>
    );
  }

  const icons = {
    image: <Image className="w-8 h-8 text-white/60" />,
    video: <Video className="w-8 h-8 text-white/60" />,
    document: <FileText className="w-8 h-8 text-white/60" />,
  };

  const labels = {
    image: t("waTemplates.mediaImage"),
    video: t("waTemplates.mediaVideo"),
    document: t("waTemplates.mediaDocument"),
  };

  return (
    <div className="w-full h-36 rounded-t-lg bg-[#0b4a3f] flex flex-col items-center justify-center gap-2">
      {icons[type]}
      <span className="text-xs text-white/50">{labels[type]}</span>
    </div>
  );
};

const ButtonPreview = ({ button }: { button: WaTemplateButton }) => {
  const icons = {
    QUICK_REPLY: <Reply className="w-3.5 h-3.5" />,
    URL: <ExternalLink className="w-3.5 h-3.5" />,
    PHONE_NUMBER: <Phone className="w-3.5 h-3.5" />,
  };

  return (
    <button className="w-full py-2 text-center text-[#53bdeb] text-[13px] font-medium flex items-center justify-center gap-1.5 border-t border-white/10">
      {icons[button.type]}
      {button.text}
    </button>
  );
};

export const WhatsAppPreview = ({
  body,
  footer,
  mediaType,
  mediaPreviewUrl,
  headerText,
  buttons,
  variableValues,
}: WhatsAppPreviewProps) => {
  const { t } = useTranslation("settings");
  const resolvedBody = resolveVariables(body, variableValues);
  const resolvedFooter = footer ? resolveVariables(footer, variableValues) : undefined;

  return (
    <div className="flex flex-col items-center">
      <div className="w-[320px] bg-[#0b141a] rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-md dark:border-[#3F3F46] dark:shadow-none">
        <div className="bg-[#202c33] px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#2a3942] flex items-center justify-center">
            <svg className="w-4 h-4 text-[#8696a0]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-[#e9edef]">+55 11 99999-0000</p>
            <p className="text-[11px] text-[#8696a0]">{t("waTemplates.previewOnline")}</p>
          </div>
        </div>

        <div
          className="px-3 py-4 min-h-[280px]"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23182229\' fill-opacity=\'0.6\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        >
          <div className="max-w-[85%] ml-auto">
            <div className="bg-[#005c4b] rounded-lg overflow-hidden shadow-sm">
              <MediaPlaceholder type={mediaType} url={mediaPreviewUrl} />

              <div className="px-2.5 pt-2 pb-1.5">
                {headerText && (
                  <p className="text-sm font-bold text-[#e9edef] mb-1">{headerText}</p>
                )}

                <p className="text-[13.5px] text-[#e9edef] leading-[19px] whitespace-pre-wrap">
                  {resolvedBody || t("waTemplates.previewEmptyBody")}
                </p>

                {resolvedFooter && (
                  <p className="text-[11.5px] text-[#8696a0] mt-1.5">{resolvedFooter}</p>
                )}

                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[11px] text-[#8696a0]">14:32</span>
                  <svg className="w-4 h-4 text-[#53bdeb]" viewBox="0 0 16 15" fill="currentColor">
                    <path d="M15.01 3.316l-.478-.372a.365.365 0 00-.51.063L8.666 9.88a.32.32 0 01-.484.033l-.358-.325a.32.32 0 00-.484.033l-.378.456a.32.32 0 00.063.439l1.04.94a.397.397 0 00.575-.063l6.004-7.042a.365.365 0 00-.063-.51zm-5.657 0l-.478-.372a.365.365 0 00-.51.063L2.997 9.88a.32.32 0 01-.484.033l-.358-.325a.32.32 0 00-.484.033l-.378.456a.32.32 0 00.063.439l1.04.94a.397.397 0 00.575-.063l6.004-7.042a.365.365 0 00-.063-.51z"/>
                  </svg>
                </div>
              </div>

              {buttons.length > 0 && (
                <div className="flex flex-col">
                  {buttons.map((btn) => (
                    <ButtonPreview key={btn.id} button={btn} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-[#202c33] px-2 py-2 flex items-center gap-2">
          <div className="flex-1 bg-[#2a3942] rounded-full px-4 py-2">
            <span className="text-[13px] text-[#8696a0]">{t("waTemplates.previewInput")}</span>
          </div>
          <div className="w-9 h-9 rounded-full bg-[#00a884] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0b141a]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8zm4-9h-3V8a1 1 0 00-2 0v3H8a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};
